/**
 * Caregiver ClientIntakesPage (CONTRACTS §6, spec §9.3): intake history
 * with statuses for a linked client, plus the distinct caregiver action
 * 'Notitie van begeleider' (repositories.medication.addCaregiverNote).
 *
 * A caregiver NEVER marks a client's intake as performed here — the note is
 * an administrative, audit-logged caregiver action only.
 */
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Banner,
  Button,
  Card,
  Dialog,
  EmptyState,
  ErrorState,
  Skeleton,
  StatusChip,
  TextArea,
  useToast,
} from '@/components/ui'
import { ChevronLeftIcon, ClockIcon, NoteIcon } from '@/components/icons'
import { repositories } from '@/services/repositories'
import { PATHS } from '@/app/router/paths'
import type { MedicationIntakeEvent } from '@/types'
import {
  errorMessage,
  formatDutchDateTime,
  occurrenceStatusMeta,
  useIntakeHistory,
} from '@/features/medication'
import './caregiver.css'

function eventTimestamp(event: MedicationIntakeEvent): string {
  if (event.takenAt) return `Ingenomen op ${formatDutchDateTime(event.takenAt)}`
  return `Geregistreerd op ${formatDutchDateTime(event.createdAt)}`
}

export default function ClientIntakesPage() {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { status, events, error, refresh } = useIntakeHistory(clientId ?? null, 100)

  const [noteTarget, setNoteTarget] = useState<MedicationIntakeEvent | null>(null)
  const [noteText, setNoteText] = useState('')
  const [noteError, setNoteError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const closeNote = () => {
    setNoteTarget(null)
    setNoteText('')
    setNoteError(null)
  }

  const saveNote = async () => {
    if (!noteTarget || saving) return
    if (!noteText.trim()) {
      setNoteError('Vul een notitie in.')
      return
    }
    setSaving(true)
    setNoteError(null)
    try {
      await repositories.medication.addCaregiverNote(noteTarget.occurrenceId, noteText.trim())
      showToast({ message: 'Notitie van begeleider opgeslagen en vastgelegd in het auditlog.', tone: 'success' })
      closeNote()
      await refresh()
    } catch (err) {
      setNoteError(errorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  if (!clientId) {
    return (
      <EmptyState
        icon={<ClockIcon size={26} />}
        title="Cliënt niet gevonden"
        action={
          <Button variant="secondary" onClick={() => navigate(PATHS.caregiver.clients)}>
            Terug naar cliënten
          </Button>
        }
      />
    )
  }

  return (
    <div className="caregiver-page">
      <button
        type="button"
        className="caregiver-back-link"
        onClick={() => navigate(PATHS.caregiver.clientDetail(clientId))}
      >
        <ChevronLeftIcon size={16} />
        Terug naar cliënt
      </button>

      <header>
        <h1 className="caregiver-section-title">Innamegeschiedenis</h1>
        <p className="caregiver-muted">
          Geregistreerde innames van deze cliënt, nieuwste eerst.
        </p>
      </header>

      <Banner tone="info">
        De cliënt registreert de inname zelf. Als begeleider kun je hier geen inname markeren —
        alleen een notitie toevoegen als aparte, vastgelegde begeleidersactie.
      </Banner>

      {status === 'loading' ? <Skeleton lines={4} /> : null}
      {status === 'unavailable' ? (
        <Card padding="md">
          <p className="caregiver-muted">Cloud niet ingesteld. De geschiedenis kan nu niet worden opgehaald.</p>
        </Card>
      ) : null}
      {status === 'error' ? <ErrorState error={error ?? undefined} onRetry={() => void refresh()} /> : null}

      {status === 'ready' ? (
        events.length === 0 ? (
          <EmptyState
            icon={<ClockIcon size={26} />}
            title="Nog geen innames geregistreerd"
            body="Zodra de cliënt medicatie als ingenomen registreert, verschijnt dat hier."
          />
        ) : (
          <Card padding="sm">
            <ul className="caregiver-list" aria-label="Innamegeschiedenis">
              {events.map((event) => {
                const meta = occurrenceStatusMeta(event.status)
                return (
                  <li key={event.id} className="caregiver-link-item">
                    <div className="caregiver-link-header">
                      <span className="caregiver-event-text">
                        <span className="caregiver-event-title">{eventTimestamp(event)}</span>
                        {event.clientNote ? (
                          <span className="caregiver-event-meta">Notitie van cliënt: {event.clientNote}</span>
                        ) : null}
                        {event.proofUploadedAt ? (
                          <span className="caregiver-event-meta">
                            Bewijsvideo geüpload op {formatDutchDateTime(event.proofUploadedAt)}
                          </span>
                        ) : null}
                      </span>
                      <StatusChip status={meta.chip}>{meta.label}</StatusChip>
                    </div>
                    <div className="caregiver-actions">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setNoteTarget(event)
                          setNoteText('')
                          setNoteError(null)
                        }}
                      >
                        <NoteIcon size={16} />
                        Notitie van begeleider
                      </Button>
                    </div>
                  </li>
                )
              })}
            </ul>
          </Card>
        )
      ) : null}

      <Dialog
        open={noteTarget !== null}
        onClose={closeNote}
        title="Notitie van begeleider"
        footer={
          <div className="medication-dialog-footer">
            <Button variant="ghost" onClick={closeNote} disabled={saving}>
              Annuleren
            </Button>
            <Button variant="primary" loading={saving} onClick={() => void saveNote()}>
              Notitie opslaan
            </Button>
          </div>
        }
      >
        <div className="medication-dialog-body">
          <p className="medication-muted">
            Dit is een administratieve notitie bij het medicatiemoment. Je markeert hiermee{' '}
            <strong>geen</strong> inname — dat kan alleen de cliënt zelf. De notitie wordt
            vastgelegd in het auditlog.
          </p>
          <TextArea
            label="Notitie"
            value={noteText}
            onChange={setNoteText}
            rows={3}
            error={noteError ?? undefined}
            placeholder="Bijvoorbeeld: besproken in het telefonisch overleg"
          />
        </div>
      </Dialog>
    </div>
  )
}
